import { memo, useCallback } from 'react';
import { IconButton, Box, CircularProgress } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DialogComponentProps } from './Dialog.types';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledSubtitle,
} from './Dialog.styles';

/**
 * Dialog Component
 *
 * Professional dialog/modal component following the green/teal design system.
 * Fully memoized for optimal performance in large ERP applications.
 *
 * @example
 * ```tsx
 * // Basic confirmation dialog
 * <Dialog
 *   open={open}
 *   onClose={handleClose}
 *   title="Confirm Delete"
 *   actions={
 *     <>
 *       <Button variant="ghost" onClick={handleClose}>Cancel</Button>
 *       <Button variant="danger" onClick={handleDelete}>Delete</Button>
 *     </>
 *   }
 * >
 *   Are you sure you want to delete this employee record?
 * </Dialog>
 *
 * // Form dialog
 * <Dialog
 *   open={open}
 *   onClose={handleClose}
 *   title="Add Employee"
 *   subtitle="Enter employee details"
 *   size="md"
 *   actions={
 *     <>
 *       <Button variant="ghost" onClick={handleClose}>Cancel</Button>
 *       <Button variant="primary" onClick={handleSubmit} loading={loading}>
 *         Save
 *       </Button>
 *     </>
 *   }
 * >
 *   <EmployeeForm />
 * </Dialog>
 * ```
 */
export const Dialog = memo<DialogComponentProps>(function Dialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  size = 'sm',
  fullScreen = false,
  showCloseButton = true,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  loading = false,
  dividers = false,
  className,
  'data-testid': dataTestId,
  ...props
}) {
  // Memoize close handler
  const handleClose = useCallback(
    (_event: any, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' && disableBackdropClick) return;
      if (reason === 'escapeKeyDown' && disableEscapeKeyDown) return;
      onClose();
    },
    [onClose, disableBackdropClick, disableEscapeKeyDown]
  );

  // Determine max width
  const maxWidth = size === 'full' ? false : size;

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={size !== 'full'}
      fullScreen={fullScreen || size === 'full'}
      className={className}
      data-testid={dataTestId}
      {...props}
    >
      {/* Dialog Title */}
      {(title || showCloseButton) && (
        <StyledDialogTitle>
          <Box>
            {title}
            {subtitle && <StyledSubtitle>{subtitle}</StyledSubtitle>}
          </Box>
          {showCloseButton && (
            <IconButton
              onClick={onClose}
              disabled={loading}
              aria-label="close"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
        </StyledDialogTitle>
      )}

      {/* Dialog Content */}
      <StyledDialogContent dividers={dividers}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          children
        )}
      </StyledDialogContent>

      {/* Dialog Actions */}
      {actions && <StyledDialogActions>{actions}</StyledDialogActions>}
    </StyledDialog>
  );
});

Dialog.displayName = 'Dialog';

export default Dialog;
